<template>
    <div class="dashboard triage-referral-page">
        <PageBanner
            title="Triage and Referral"
            subtext="Complete referral acknowledgement and triage slip workflows."
        />

        <main class="triage-referral-content">
            <q-banner v-if="error" rounded class="bg-red-1 text-negative q-mb-md">
                {{ error }}
            </q-banner>

            <section class="triage-referral-actions" aria-label="Start triage referral workflows">
                <article
                    v-for="card in workflowCards"
                    :key="card.key"
                    class="triage-referral-action-card"
                >
                    <div class="triage-referral-action-card__main">
                        <div class="triage-referral-action-card__icon">
                            <q-icon :name="card.icon" size="28px" />
                        </div>
                        <div class="triage-referral-action-card__copy">
                            <div class="triage-referral-action-card__meta">{{ card.count }} records</div>
                            <div class="triage-referral-action-card__title-row">
                                <h2 class="triage-referral-action-card__title">{{ card.title }}</h2>
                                <q-btn
                                    class="triage-referral-action-card__button"
                                    color="primary"
                                    no-caps
                                    unelevated
                                    icon="add"
                                    :label="card.actionLabel"
                                    :disable="card.disabled"
                                    @click="openStartDialog(card.key)"
                                />
                            </div>
                        </div>
                    </div>
                </article>
            </section>

            <section class="triage-referral-panels">
                <q-expansion-item
                    default-opened
                    class="expansion-panel triage-referral-panel"
                    header-class="expansion-header triage-referral-panel__header"
                >
                    <template v-slot:header>
                        <q-item-section avatar>
                            <q-icon color="primary" name="fact_check" />
                        </q-item-section>
                        <q-item-section>
                            <q-item-label>Referral Acknowledgements</q-item-label>
                            <q-item-label caption>{{ referralRows.length }} records</q-item-label>
                        </q-item-section>
                    </template>
                    <div class="expansion-content triage-referral-panel__content">
                        <q-table
                            class="client-table triage-referral-table"
                            row-key="formResponseId"
                            :rows="referralRows"
                            :columns="referralColumns"
                            :filter="referralFilter"
                            :loading="loading"
                            :pagination="{ rowsPerPage: 8 }"
                            :rows-per-page-options="[8, 15, 25]"
                        >
                            <template v-slot:top-left>
                                <div class="users-title view-user-title triage-referral-table-tools">
                                    <q-input outlined dense debounce="300" v-model="referralFilter" placeholder="Search Referrals">
                                        <template v-slot:append>
                                            <q-icon name="close" @click="referralFilter = ''" class="cursor-pointer" />
                                            <q-icon name="search" />
                                        </template>
                                    </q-input>
                                </div>
                            </template>
                            <template v-slot:top-right>
                                <div class="users-title view-user-title triage-referral-table-count">
                                    <span>{{ referralRows.length }} referral acknowledgement records</span>
                                </div>
                            </template>
                            <template v-slot:body-cell-actions="props">
                                <q-td :props="props">
                                    <div class="row justify-end q-gutter-sm nowrap">
                                        <q-btn flat dense color="primary" icon="o_visibility" @click="openExistingForm('referralAcknowledgement', props.row)">
                                            <q-tooltip class="text-body2">View Response</q-tooltip>
                                        </q-btn>
                                        <q-btn
                                            flat
                                            dense
                                            color="accent"
                                            icon="download"
                                            :loading="downloadingId === props.row.formResponseId"
                                            :disable="downloadingId === props.row.formResponseId"
                                            @click="downloadPdf('referralAcknowledgement', props.row)"
                                        >
                                            <q-tooltip class="text-body2">Download PDF</q-tooltip>
                                        </q-btn>
                                    </div>
                                </q-td>
                            </template>
                        </q-table>
                    </div>
                </q-expansion-item>

                <q-expansion-item
                    default-opened
                    class="expansion-panel triage-referral-panel"
                    header-class="expansion-header triage-referral-panel__header"
                >
                    <template v-slot:header>
                        <q-item-section avatar>
                            <q-icon color="primary" name="assignment_turned_in" />
                        </q-item-section>
                        <q-item-section>
                            <q-item-label>Triage Slips</q-item-label>
                            <q-item-label caption>{{ triageRows.length }} records</q-item-label>
                        </q-item-section>
                    </template>
                    <div class="expansion-content triage-referral-panel__content">
                        <q-table
                            class="client-table triage-referral-table"
                            row-key="formResponseId"
                            :rows="triageRows"
                            :columns="triageColumns"
                            :filter="triageFilter"
                            :loading="loading"
                            :pagination="{ rowsPerPage: 8 }"
                            :rows-per-page-options="[8, 15, 25]"
                        >
                            <template v-slot:top-left>
                                <div class="users-title view-user-title triage-referral-table-tools">
                                    <q-input outlined dense debounce="300" v-model="triageFilter" placeholder="Search Triage Slips">
                                        <template v-slot:append>
                                            <q-icon name="close" @click="triageFilter = ''" class="cursor-pointer" />
                                            <q-icon name="search" />
                                        </template>
                                    </q-input>
                                </div>
                            </template>
                            <template v-slot:top-right>
                                <div class="users-title view-user-title triage-referral-table-count">
                                    <span>{{ triageRows.length }} triage slip records</span>
                                </div>
                            </template>
                            <template v-slot:body-cell-actions="props">
                                <q-td :props="props">
                                    <div class="row justify-end q-gutter-sm nowrap">
                                        <q-btn flat dense color="primary" icon="o_visibility" @click="openExistingForm('triageSlip', props.row)">
                                            <q-tooltip class="text-body2">View Response</q-tooltip>
                                        </q-btn>
                                        <q-btn
                                            flat
                                            dense
                                            color="accent"
                                            icon="download"
                                            :loading="downloadingId === props.row.formResponseId"
                                            :disable="downloadingId === props.row.formResponseId"
                                            @click="downloadPdf('triageSlip', props.row)"
                                        >
                                            <q-tooltip class="text-body2">Download PDF</q-tooltip>
                                        </q-btn>
                                    </div>
                                </q-td>
                            </template>
                        </q-table>
                    </div>
                </q-expansion-item>
            </section>
        </main>

        <q-dialog v-model="showStartDialog">
            <q-card style="width: min(800px, 95vw); max-width: 95vw;">
                <q-card-section class="row items-center justify-between">
                    <div class="text-h6">{{ startDialogTitle }}</div>
                    <q-btn flat round dense icon="close" v-close-popup />
                </q-card-section>
                <q-separator />
                <q-card-section>
                    <TkPersonSelect
                        v-model="selectedPerson"
                        origin-service="triage_referral"
                    />
                </q-card-section>
                <q-card-actions align="right">
                    <q-btn flat no-caps color="negative" label="Cancel" v-close-popup />
                    <q-btn
                        unelevated
                        no-caps
                        color="primary"
                        label="Start form"
                        :disable="!selectedPerson"
                        @click="startSelectedForm"
                    />
                </q-card-actions>
            </q-card>
        </q-dialog>

        <q-dialog v-model="showFormDialog" @hide="onFormDialogHide">
            <q-card class="triage-referral-form-dialog">
                <q-card-section class="triage-referral-form-dialog__body">
                    <div class="triage-referral-form-dialog__content">
                        <DynamicFormRenderer
                            v-if="showFormDialog && selectedFormId && subject"
                            :key="`${selectedFormId}-${selectedResponseId || 'new'}-${subject.id}`"
                            :formId="selectedFormId"
                            :subject="subject"
                            :_responseId="selectedResponseId"
                            :show-opt-out="false"
                            @exit="showFormDialog = false"
                            @form-submitted="refresh"
                        />
                    </div>
                </q-card-section>
            </q-card>
        </q-dialog>
    </div>
</template>

<script>
import api from "@/services/api/api";
import notify from "@/services/util/notify";
import DynamicFormRenderer from "@/components/DynamicFormComponents/DynamicFormRenderer.vue";
import PageBanner from "@/components/ClientComponents/DashboardComponents/PageBanner.vue";
import TkPersonSelect from "@/components/ClientComponents/TemporaryClients/TkPersonSelect.vue";
import { tkPersonSubject } from "@/services/util/formSubject";

const FIELD_IDS = {
    referralAcknowledgement: {
        referralDate: "TR_REFERRAL_RECEIVED_DATE",
        name: "TR_TANGATA_WHAIORA_NAME",
        service: "TR_SERVICE_REFERRED_INTO",
        outcome: "TR_REFERRAL_OUTCOME",
    },
    triageSlip: {
        referralDate: "TRIAGE_REFERRAL_RECEIVED_DATE",
        name: "TRIAGE_TANGATA_WHAIORA_NAME",
        dateOfBirth: "TRIAGE_DATE_OF_BIRTH",
        nhi: "TRIAGE_NHI",
        needs: "TRIAGE_IDENTIFIED_NEEDS",
        readyDate: "TRIAGE_READY_DATE",
        outcome: "TRIAGE_OUTCOME",
    },
};

const personName = (row) => row?.preferred_name || [row?.first_name, row?.last_name].filter(Boolean).join(" ").trim() || "-";

export default {
    name: "TriageReferral",
    components: { DynamicFormRenderer, PageBanner, TkPersonSelect },
    data() {
        return {
            loading: false,
            error: null,
            referralFilter: "",
            triageFilter: "",
            formIds: {
                referralAcknowledgement: null,
                triageSlip: null,
            },
            referralRows: [],
            triageRows: [],
            selectedFormKey: null,
            selectedPerson: null,
            selectedResponseId: null,
            showStartDialog: false,
            showFormDialog: false,
            downloadingId: null,
        };
    },
    computed: {
        workflowCards() {
            return [
                {
                    key: "referralAcknowledgement",
                    title: "Referral Acknowledgements",
                    description: "Create an acknowledgement, review previous responses, and export signed records.",
                    actionLabel: "Start referral",
                    icon: "fact_check",
                    count: this.referralRows.length,
                    disabled: !this.formIds.referralAcknowledgement,
                },
                {
                    key: "triageSlip",
                    title: "Triage Slips",
                    description: "Start a triage slip, track readiness, and keep outcome details close at hand.",
                    actionLabel: "Start triage slip",
                    icon: "assignment_turned_in",
                    count: this.triageRows.length,
                    disabled: !this.formIds.triageSlip,
                },
            ];
        },
        referralColumns() {
            return [
                { name: "personName", label: "Client", field: "personName", align: "left", sortable: true },
                { name: "phone", label: "Phone", field: "phone", align: "left" },
                { name: "email", label: "Email", field: "email", align: "left" },
                { name: "referralDate", label: "Referral received", field: "referralDate", align: "left", sortable: true },
                { name: "service", label: "Service", field: "service", align: "left", sortable: true },
                { name: "outcome", label: "Outcome", field: "outcome", align: "left", sortable: true },
                { name: "updatedAt", label: "Updated", field: "updatedAt", align: "left", sortable: true },
                { name: "status", label: "Status", field: "status", align: "left", sortable: true },
                { name: "actions", label: "Actions", field: "actions", align: "right" },
            ];
        },
        triageColumns() {
            return [
                { name: "personName", label: "Client", field: "personName", align: "left", sortable: true },
                { name: "dateOfBirth", label: "DOB", field: "dateOfBirth", align: "left", sortable: true },
                { name: "nhi", label: "NHI", field: "nhi", align: "left" },
                { name: "referralDate", label: "Referral received", field: "referralDate", align: "left", sortable: true },
                { name: "readyDate", label: "Ready for triage", field: "readyDate", align: "left", sortable: true },
                { name: "outcome", label: "Outcome", field: "outcome", align: "left", sortable: true },
                { name: "updatedAt", label: "Updated", field: "updatedAt", align: "left", sortable: true },
                { name: "status", label: "Status", field: "status", align: "left", sortable: true },
                { name: "actions", label: "Actions", field: "actions", align: "right" },
            ];
        },
        selectedFormId() {
            return this.formIds[this.selectedFormKey] || null;
        },
        subject() {
            return this.selectedPerson ? tkPersonSubject(this.selectedPerson) : null;
        },
        startDialogTitle() {
            return this.selectedFormKey === "triageSlip" ? "Start Triage Slip" : "Start Referral Acknowledgement";
        },
    },
    async mounted() {
        await this.refresh();
    },
    methods: {
        async refresh() {
            this.loading = true;
            this.error = null;

            try {
                const config = await api.forms.getTriageReferralConfig();
                this.formIds = config.data.forms;

                const [referrals, triage] = await Promise.all([
                    api.forms.getTriageReferralResponses("referralAcknowledgement"),
                    api.forms.getTriageReferralResponses("triageSlip"),
                ]);
                this.referralRows = referrals.data.map(row => this.mapReferralRow(row));
                this.triageRows = triage.data.map(row => this.mapTriageRow(row));
            } catch (error) {
                this.error = "Failed to load triage and referral responses.";
            } finally {
                this.loading = false;
            }
        },
        mapReferralRow(row) {
            const fields = row.fieldValues || {};
            return {
                ...row,
                personName: personName(row),
                referralDate: this.formatDate(fields[FIELD_IDS.referralAcknowledgement.referralDate]),
                service: this.formatValue(fields[FIELD_IDS.referralAcknowledgement.service]),
                outcome: this.formatValue(fields[FIELD_IDS.referralAcknowledgement.outcome]),
                updatedAt: this.formatDateTime(row.updated_at),
                status: this.formatValue(row.status),
            };
        },
        mapTriageRow(row) {
            const fields = row.fieldValues || {};
            return {
                ...row,
                personName: personName(row),
                dateOfBirth: this.formatDate(fields[FIELD_IDS.triageSlip.dateOfBirth] || row.date_of_birth),
                nhi: fields[FIELD_IDS.triageSlip.nhi] || row.nhi || "-",
                referralDate: this.formatDate(fields[FIELD_IDS.triageSlip.referralDate]),
                readyDate: this.formatDate(fields[FIELD_IDS.triageSlip.readyDate]),
                outcome: this.formatValue(fields[FIELD_IDS.triageSlip.outcome]),
                updatedAt: this.formatDateTime(row.updated_at),
                status: this.formatValue(row.status),
            };
        },
        openStartDialog(formKey) {
            this.selectedFormKey = formKey;
            this.selectedPerson = null;
            this.selectedResponseId = null;
            this.showStartDialog = true;
        },
        startSelectedForm() {
            if (!this.selectedPerson || !this.selectedFormId) return;
            this.showStartDialog = false;
            this.showFormDialog = true;
        },
        openExistingForm(formKey, row) {
            if (!row?.formResponseId) return;

            this.selectedFormKey = formKey;
            this.selectedResponseId = row.formResponseId;
            this.selectedPerson = {
                id: row.tkPersonId,
                first_name: row.first_name,
                last_name: row.last_name,
                preferred_name: row.preferred_name,
                date_of_birth: row.date_of_birth,
                nhi: row.nhi,
                phone: row.phone,
                email: row.email,
            };
            this.showFormDialog = true;
        },
        async downloadPdf(formKey, row) {
            if (!row?.formResponseId || !this.formIds[formKey]) return;

            this.downloadingId = row.formResponseId;
            try {
                await api.forms.exportFormResponsePdf(
                    this.formIds[formKey],
                    row.formResponseId,
                    row.personName
                );
            } catch (error) {
                notify.error("Failed to download PDF", "top");
            } finally {
                this.downloadingId = null;
            }
        },
        async onFormDialogHide() {
            this.selectedResponseId = null;
            await this.refresh();
        },
        formatDate(value) {
            if (!value) return "-";
            const date = new Date(value);
            if (Number.isNaN(date.getTime())) return value;
            return date.toLocaleDateString("en-nz");
        },
        formatDateTime(value) {
            if (!value) return "-";
            const date = new Date(value);
            if (Number.isNaN(date.getTime())) return value;
            return date.toLocaleString("en-nz");
        },
        formatValue(value) {
            if (!value) return "-";
            return String(value)
                .replace(/_/g, " ")
                .split(" ")
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(" ");
        },
    },
};
</script>

<style>
@import "../assets/styles/clientDemographics.css";
</style>

<style scoped>
.triage-referral-page {
    width: 100%;
    min-height: 100vh;
    background: rgba(202, 210, 197, 0.22);
}

.triage-referral-content {
    width: 100%;
    padding: 22px;
    background: transparent;
}

.triage-referral-actions {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 18px;
    margin-bottom: 22px;
}

.triage-referral-action-card {
    position: relative;
    overflow: hidden;
    min-width: 0;
    padding: 18px;
    background:
        linear-gradient(135deg, rgba(255, 255, 255, 0.98), rgba(248, 250, 248, 0.94));
    border: 1px solid rgba(82, 121, 111, 0.16);
    border-radius: 8px;
    box-shadow: 0 12px 32px rgba(47, 62, 70, 0.12);
    display: flex;
    align-items: center;
}

.triage-referral-action-card::before {
    content: "";
    position: absolute;
    inset: 0 auto 0 0;
    width: 4px;
    background: linear-gradient(180deg, #84a98c, #52796f);
}

.triage-referral-action-card__main {
    min-width: 0;
    width: 100%;
    display: flex;
    align-items: center;
    gap: 14px;
}

.triage-referral-action-card__icon {
    width: 52px;
    height: 52px;
    color: #24454a;
    background: #edf4ef;
    border: 1px solid rgba(82, 121, 111, 0.14);
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
}

.triage-referral-action-card__copy {
    width: 100%;
    min-width: 0;
}

.triage-referral-action-card__meta {
    margin-bottom: 4px;
    color: #52796f;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
}

.triage-referral-action-card__title {
    margin: 0;
    color: #2f3e46;
    font-size: 1.16rem;
    font-weight: 700;
    line-height: 1.2;
    min-width: 0;
}

.triage-referral-action-card__title-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
}

.triage-referral-action-card__button {
    min-width: 164px;
    border-radius: 6px;
    flex: 0 0 auto;
}

.triage-referral-panels {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.triage-referral-panel {
    margin-bottom: 0;
    background: white;
    box-shadow: 0 8px 24px rgba(47, 62, 70, 0.08);
}

.triage-referral-panel__content {
    padding: 1rem;
}

.triage-referral-table {
    width: 100%;
    box-shadow: 0 10px 34px -18px rgba(0, 0, 0, 0.24);
}

.triage-referral-table-tools {
    margin-bottom: 0;
    justify-content: flex-start;
}

.triage-referral-table-count {
    margin-bottom: 0;
    justify-content: flex-end;
    color: #6c757d;
    font-weight: 600;
}

.triage-referral-table :deep(.q-table__top) {
    padding: 12px 16px;
}

.triage-referral-table :deep(.q-table__middle) {
    overflow: auto;
}

.triage-referral-form-dialog {
    width: min(1280px, 94vw);
    max-width: 96vw;
    max-height: 92vh;
    background: white;
}

.triage-referral-form-dialog__body {
    max-height: 92vh;
    overflow: auto;
    display: flex;
    justify-content: center;
    padding: 16px;
}

.triage-referral-form-dialog__content {
    width: min(980px, 100%);
    min-width: 0;
    margin: 0 auto;
}

@media (max-width: 1100px) {
    .triage-referral-actions {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 700px) {
    .triage-referral-content {
        padding: 14px 12px;
    }

    .triage-referral-actions {
        gap: 12px;
        margin-bottom: 14px;
    }

    .triage-referral-action-card {
        padding: 14px;
    }

    .triage-referral-action-card__main {
        align-items: stretch;
        flex-direction: column;
    }

    .triage-referral-action-card__title-row {
        align-items: stretch;
        flex-direction: column;
        gap: 10px;
    }

    .triage-referral-action-card__button {
        width: 100%;
    }

    .triage-referral-panel__content {
        padding: 0.75rem;
    }

    .triage-referral-table-tools,
    .triage-referral-table-count {
        width: 100%;
        justify-content: flex-start;
    }

    .triage-referral-form-dialog {
        width: 100vw;
        max-width: 100vw;
        height: 100vh;
        max-height: 100vh;
    }

    .triage-referral-form-dialog__body {
        padding: 8px;
    }

    .triage-referral-form-dialog__content {
        width: 100%;
    }
}
</style>
